import { ReactElement, useMemo, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Checkbox, Divider, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";

type MarkerGroups = Record<string, { positive: string[]; negative: string[] }>;

const PBMC_MARKERS: MarkerGroups = {
  "CD14+ Monocytes": { positive: ["FCN1", "CD14", "LYZ", "S100A8", "S100A9"], negative: [] },
  "CD16+ Monocytes": { positive: ["FCGR3A", "LYN", "LST1", "MS4A7"], negative: [] },
  "Dendritic cells": { positive: ["CST3", "FCER1A", "CLEC10A"], negative: [] },
  "NK cells": { positive: ["GNLY", "NKG7", "FCER1G", "TYROBP", "KLRD1"], negative: [] },
  "B cells": { positive: ["MS4A1", "CD79A", "CD37", "CD74", "HLA-DRA"], negative: [] },
  "Plasma cells": { positive: ["MZB1", "JCHAIN", "PRDM1", "XBP1", "IGKC"], negative: ["PAX5"] },
  "CD4+ T cells": { positive: ["CD3D", "CD3E", "CD4", "IL7R", "LTB"], negative: [] },
  "CD8+ T cells": { positive: ["CD3D", "CD8A", "CD8B", "CCL5", "GZMK"], negative: [] },
  "Naive T cells": { positive: ["CCR7", "LEF1", "TCF7", "MALAT1"], negative: [] },
  pDC: { positive: ["GZMB", "IL3RA", "COBLL1", "TCF4"], negative: [] },
};

const REFERENCES: Record<string, { name: string; markers: MarkerGroups }> = {
  breast_tumor: { name: "Breast tumor microenvironment", markers: {
    "Epithelial / tumor cells": { positive: ["EPCAM", "KRT8", "KRT18", "KRT19"], negative: [] },
    "T cells": { positive: ["CD3D", "CD3E", "TRBC1"], negative: [] },
    "B cells": { positive: ["MS4A1", "CD79A", "CD74"], negative: [] },
    "Myeloid cells": { positive: ["LYZ", "LST1", "FCER1G"], negative: [] },
    "Endothelial cells": { positive: ["PECAM1", "VWF", "KDR"], negative: [] },
    "Fibroblasts": { positive: ["COL1A1", "COL1A2", "DCN"], negative: [] },
  }},
  aml_bone_marrow: { name: "AML bone marrow", markers: {
    "HSPC / blasts": { positive: ["CD34", "KIT", "GATA2"], negative: [] },
    "Myeloid / AML cells": { positive: ["MPO", "ELANE", "AZU1"], negative: [] },
    "Monocytes": { positive: ["LYZ", "LST1", "FCN1"], negative: [] },
    "Erythroid cells": { positive: ["HBB", "HBA1", "GYPA"], negative: [] },
    "T cells": { positive: ["CD3D", "CD3E", "TRBC1"], negative: [] },
    "B cells": { positive: ["MS4A1", "CD79A", "CD74"], negative: [] },
    "NK cells": { positive: ["NKG7", "GNLY", "KLRD1"], negative: [] },
  }},
  mafld_liver: { name: "MAFLD / liver", markers: {
    "Kupffer cells": { positive: ["C1QC", "C1QA", "MARCO"], negative: [] },
    "Monocyte-derived macrophages": { positive: ["FCN1", "S100A8", "S100A9"], negative: [] },
    "Hepatocytes": { positive: ["ALB", "APOA1", "TTR"], negative: [] },
    "Endothelial cells": { positive: ["PECAM1", "VWF", "KDR"], negative: [] },
    "Hepatic stellate cells": { positive: ["COL1A1", "COL3A1", "DCN"], negative: [] },
    "T / NK cells": { positive: ["CD3D", "NKG7", "KLRD1"], negative: [] },
    "B cells": { positive: ["MS4A1", "CD79A", "CD74"], negative: [] },
  }},
  pbmc_immune: { name: "PBMC immune cells", markers: PBMC_MARKERS },
};

const inferReference = (context: string) => {
  const value = context.toLowerCase();
  if (/breast|mammary/.test(value)) return "breast_tumor";
  if (/\baml\b|leukemia|bone marrow|lsd1/.test(value)) return "aml_bone_marrow";
  if (/mafld|fatty liver|steatos|hepatic|\bliver\b/.test(value)) return "mafld_liver";
  return "pbmc_immune";
};

const annotationGroups = (result: any, fallback: MarkerGroups): MarkerGroups | null => {
  const source = result?.basicResults ?? result ?? {};
  const labels = Array.isArray(source.uniqueAnnotations) ? source.uniqueAnnotations.map(String).filter(Boolean) : [];
  if (!labels.length) return null;
  const savedMarkers = source.annotationMarkers ?? {};
  const fallbackEntries = Object.entries(fallback);
  return Object.fromEntries(labels.map((label: string) => {
    const key = label.toLowerCase();
    const related = fallbackEntries.find(([name]) => {
      const candidate = name.toLowerCase();
      return candidate.includes(key) || key.includes(candidate.replace(" cells", ""));
    });
    const genes = Array.isArray(savedMarkers[label]) ? savedMarkers[label] : related?.[1].positive ?? [];
    return [label, { positive: genes.map(String), negative: related?.[1].negative ?? [] }];
  }));
};

const MarkerGeneSetForm = ({ onSubmit, onBack, className = "", projectContext = "", annotationResult }: any): ReactElement => {
  const initialReference = useMemo(() => inferReference(projectContext), [projectContext]);
  const dynamicMarkers = useMemo(() => annotationGroups(annotationResult, REFERENCES[initialReference].markers), [annotationResult, initialReference]);
  const initialKey = dynamicMarkers ? "annotation_dynamic" : initialReference;
  const initialMarkers = dynamicMarkers ?? REFERENCES[initialReference].markers;
  const [clusterKey, setClusterKey] = useState("leiden");
  const [reference, setReference] = useState(initialKey);
  const [selected, setSelected] = useState<string[]>(Object.keys(initialMarkers));
  const [markers, setMarkers] = useState<MarkerGroups>(initialMarkers);
  const groups = useMemo(() => Object.keys(markers), [markers]);
  const selectReference = (key: string) => { const next = key === "annotation_dynamic" && dynamicMarkers ? dynamicMarkers : REFERENCES[key].markers; setReference(key); setMarkers(next); setSelected(Object.keys(next)); };
  const toggle = (name: string) => setSelected((old) => old.includes(name) ? old.filter((x) => x !== name) : [...old, name]);
  const edit = (name: string, role: "positive" | "negative", value: string) => setMarkers((old) => ({ ...old, [name]: { ...old[name], [role]: value.split(/[,\n]/).map((x) => x.trim().toUpperCase()).filter(Boolean) } }));
  const submit = (event: React.FormEvent) => { event.preventDefault(); onSubmit?.({ cluster_key: clusterKey, reference, reference_name: reference === "annotation_dynamic" ? "Saved annotation cell types" : REFERENCES[reference].name, cell_types: Object.fromEntries(selected.map((name) => [name, markers[name]])) }); };
  return <Box className={`flex flex-col h-full ${className}`}>
    <Box className="flex-1 overflow-auto p-6">
      <Typography variant="body1" sx={{ mb: 3 }}>Explore known marker genes across clusters to help identify the biological cell types present in the sample.</Typography>
      <form onSubmit={submit}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 3 }}>
          <FormControl fullWidth><InputLabel>Cluster result</InputLabel><Select value={clusterKey} label="Cluster result" onChange={(e) => setClusterKey(e.target.value)}><MenuItem value="leiden">Leiden clusters</MenuItem></Select></FormControl>
          <FormControl fullWidth><InputLabel>Marker reference</InputLabel><Select value={reference} label="Marker reference" onChange={(e) => selectReference(e.target.value)}>{dynamicMarkers && <MenuItem value="annotation_dynamic">Saved annotation cell types</MenuItem>}{Object.entries(REFERENCES).map(([key, item]) => <MenuItem key={key} value={key}>{item.name}</MenuItem>)}</Select></FormControl>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mr: "auto" }}>Cell types to inspect</Typography>
          <Typography variant="caption" color="text.secondary">{selected.length} of {groups.length} selected</Typography>
          <Button size="small" onClick={() => setSelected(groups)}>Select all</Button>
          <Button size="small" onClick={() => setSelected([])}>Clear</Button>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, mt: 1 }}>
          {groups.map((name) => <FormControlLabel key={name} control={<Checkbox checked={selected.includes(name)} onChange={() => toggle(name)} />} label={name} />)}
        </Box>
        <Accordion sx={{ mt: 2 }}><AccordionSummary>Advanced marker settings</AccordionSummary><AccordionDetails>
          {selected.map((name) => <Box key={name} sx={{ mb: 2 }}><Typography variant="subtitle2">{name}</Typography><Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}><TextField size="small" label="Positive markers" value={markers[name].positive.join(", ")} onChange={(e) => edit(name, "positive", e.target.value)} /><TextField size="small" label="Negative markers" value={markers[name].negative.join(", ")} onChange={(e) => edit(name, "negative", e.target.value)} /></Box></Box>)}
          {!selected.length && <Typography variant="body2" color="text.secondary">Select at least one cell type to edit its marker genes.</Typography>}
          <Button onClick={() => selectReference(reference)}>Reset selected reference</Button>
        </AccordionDetails></Accordion>
        <Divider sx={{ my: 3 }} />
      </form>
    </Box>
    <Box className="flex justify-between items-center p-6 border-t"><Button variant="outlined" onClick={onBack}>Back</Button><Button variant="contained" disabled={!selected.length} onClick={submit}>Generate Marker Expression Plot</Button></Box>
  </Box>;
};

export default MarkerGeneSetForm;
